// MARK://dhk8/admin-shell/lib/clients/users
// Personal del negocio (staff / roles / PINs del POS) → dhk8-platform-auth:
//   GET    /api/users           ?role=  → [{ id, email, fullName, role, status, pinId, createdAt }]
//   GET    /api/users/:id
// F6.2 R3d / ADR-0009 — ESCRITURAS por el grafo (mutations gateadas server-side
// con el MISMO permiso RBAC de la ruta REST: platform-auth:user:{create,update,
// delete}@*). Reemplazan POST/PUT/DELETE /api/users[/:id[/role]] y PUT /v1/users/:id/pin.
//   createStaffUser / updateStaffUser / setStaffUserRole / deleteStaffUser / setStaffUserPin
// El tenant activo viaja en x-dhk8-tenant-id (lo inyecta gqlClient desde la sesión).
// Preview: copia mutable de mock-data.
import { PREVIEW_STUB, mock } from '../env.js'
import { gqlClient } from '../gql.js'
import {
  AdminStaffUsersQuery, AdminStaffUserQuery,
  CreateStaffUserMutation, UpdateStaffUserMutation, SetStaffUserRoleMutation,
  DeleteStaffUserMutation, SetStaffUserPinMutation,
} from '../queries/users.queries.js'
import * as M from '../mock-data.js'


// ── Roles (espejo de dhk8-platform-auth STAFF_ROLES) ───────────────────────
export const STAFF_ROLES = ['owner', 'manager', 'cashier', 'waiter', 'kitchen'] as const
export type StaffRole = (typeof STAFF_ROLES)[number]
export const STAFF_ROLE_LABEL: Record<StaffRole, string> = {
  owner: 'Dueño',
  manager: 'Gerente',
  cashier: 'Cajero',
  waiter: 'Mesero',
  kitchen: 'Cocina',
}
// Compat con código previo.
export const ROLES = STAFF_ROLES

export type StaffStatus = 'ACTIVE' | 'SUSPENDED' | 'DELETED'

export interface StaffUser {
  id: string
  email: string | null
  fullName: string
  role: string
  status: StaffStatus
  pinId: string | null
  /** true si tiene PIN de gerente configurado (para overrides del POS). */
  hasManagerPin?: boolean
  createdAt?: string
}
export interface CreateStaffInput {
  email?: string | null
  fullName: string
  role: StaffRole
  pinId?: string  // 4-digit "ID de POS" — minted server-side if omitted
  pin?: string    // 6-digit POS PIN
}
export interface UpdateStaffInput {
  fullName?: string
  email?: string
  role?: StaffRole
  status?: StaffStatus
}

// ── adapter API ↔ UI AdminUser ─────────────────────────────────────────────
function toUi(u: StaffUser): M.AdminUser {
  return { id: u.id, fullName: u.fullName, email: u.email ?? '', role: u.role, pinId: u.pinId, active: u.status === 'ACTIVE' }
}

// ── preview store ──────────────────────────────────────────────────────────
const db = { users: structuredClone(M.USERS) }
const mkid = () => `u-${Math.random().toString(36).slice(2, 8)}`

// ── reads ──────────────────────────────────────────────────────────────────
export async function listUsers(role?: StaffRole, signal?: AbortSignal): Promise<StaffUser[]> {
  if (PREVIEW_STUB) {
    const rows = db.users.filter((u) => !role || u.role === role).map((u): StaffUser => ({ id: u.id, email: u.email || null, fullName: u.fullName, role: u.role, status: u.active ? 'ACTIVE' : 'SUSPENDED', pinId: u.pinId }))
    return mock(rows)
  }
  // F3.2 / ADR-0009: lectura por el grafo (platform-auth, tenant del JWT). Escrituras
  // (crear/editar/rol/PIN/borrar) + passkeys siguen REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminStaffUsersQuery, role ? { role } : {}, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.staffUsers ?? []).map((u): StaffUser => ({
    id: u.id, email: u.email, fullName: u.fullName, role: u.role,
    status: u.status as StaffStatus, pinId: u.pinId, hasManagerPin: u.hasManagerPin,
    createdAt: u.createdAt,
  }))
}
export async function getUser(id: string, signal?: AbortSignal): Promise<StaffUser> {
  if (PREVIEW_STUB) { const u = db.users.find((x) => x.id === id); if (!u) throw new Error('Usuario no encontrado'); return mock({ id: u.id, email: u.email || null, fullName: u.fullName, role: u.role, status: u.active ? 'ACTIVE' : 'SUSPENDED', pinId: u.pinId } as StaffUser) }
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminStaffUserQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const u = res.data?.staffUser
  if (!u) throw new Error('Usuario no encontrado')
  return { id: u.id, email: u.email, fullName: u.fullName, role: u.role, status: u.status as StaffStatus, pinId: u.pinId, hasManagerPin: u.hasManagerPin, createdAt: u.createdAt }
}

// ── writes ─────────────────────────────────────────────────────────────────
/** Returns the created user; `pin` is the plaintext 6-digit PIN (shown once) or null. */
export async function createUser(input: CreateStaffInput): Promise<StaffUser & { pin: string | null }> {
  if (PREVIEW_STUB) {
    const u: M.AdminUser = { id: mkid(), fullName: input.fullName, email: input.email ?? '', role: input.role, pinId: input.pinId ?? String(1000 + db.users.length), active: true }
    db.users.push(u)
    return mock({ id: u.id, email: u.email || null, fullName: u.fullName, role: u.role, status: 'ACTIVE' as const, pinId: u.pinId, pin: input.pin ?? '123456' })
  }
  const res = await gqlClient.mutation(CreateStaffUserMutation, {
    input: {
      ...(input.email != null ? { email: input.email } : {}),
      fullName: input.fullName,
      role: input.role,
      ...(input.pinId != null ? { pinId: input.pinId } : {}),
      ...(input.pin != null ? { pin: input.pin } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.createStaffUser
  if (!r) throw new Error('No se pudo crear el usuario')
  const u = r.user
  return { id: u.id, email: u.email, fullName: u.fullName, role: u.role, status: u.status as StaffStatus, pinId: u.pinId, createdAt: u.createdAt, pin: r.pin }
}
export async function updateUser(id: string, input: UpdateStaffInput): Promise<StaffUser> {
  if (PREVIEW_STUB) {
    const u = db.users.find((x) => x.id === id)
    if (u) { if (input.fullName !== undefined) u.fullName = input.fullName; if (input.email !== undefined) u.email = input.email; if (input.role !== undefined) u.role = input.role; if (input.status !== undefined) u.active = input.status === 'ACTIVE' }
    return mock({ id, email: u?.email || null, fullName: u?.fullName ?? '', role: u?.role ?? 'waiter', status: u?.active ? 'ACTIVE' as const : 'SUSPENDED' as const, pinId: u?.pinId ?? null })
  }
  const res = await gqlClient.mutation(UpdateStaffUserMutation, {
    id,
    input: {
      ...(input.fullName !== undefined ? { fullName: input.fullName } : {}),
      ...(input.email !== undefined ? { email: input.email } : {}),
      ...(input.role !== undefined ? { role: input.role } : {}),
      ...(input.status !== undefined ? { status: input.status } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const u = res.data?.updateStaffUser
  if (!u) throw new Error('No se pudo actualizar el usuario')
  return { id: u.id, email: u.email, fullName: u.fullName, role: u.role, status: u.status as StaffStatus, pinId: u.pinId, hasManagerPin: u.hasManagerPin, createdAt: u.createdAt }
}
export async function setUserRole(id: string, role: StaffRole): Promise<StaffUser> {
  if (PREVIEW_STUB) { const u = db.users.find((x) => x.id === id); if (u) u.role = role; return mock({ id, email: u?.email || null, fullName: u?.fullName ?? '', role, status: u?.active ? 'ACTIVE' as const : 'SUSPENDED' as const, pinId: u?.pinId ?? null }) }
  const res = await gqlClient.mutation(SetStaffUserRoleMutation, { id, role }).toPromise()
  if (res.error) throw res.error
  const u = res.data?.setStaffUserRole
  if (!u) throw new Error('No se pudo cambiar el rol')
  return { id: u.id, email: u.email, fullName: u.fullName, role: u.role, status: u.status as StaffStatus, pinId: u.pinId, hasManagerPin: u.hasManagerPin, createdAt: u.createdAt }
}
export async function deleteUser(id: string): Promise<{ id: string; status: StaffStatus }> {
  if (PREVIEW_STUB) { db.users = db.users.filter((x) => x.id !== id); return mock({ id, status: 'DELETED' as const }) }
  const res = await gqlClient.mutation(DeleteStaffUserMutation, { id }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.deleteStaffUser
  if (!r) throw new Error('No se pudo eliminar el usuario')
  return { id: r.id, status: r.status as StaffStatus }
}
/**
 * Set / reset a user's POS credential. `pinId` = ID de POS; `pin` = PIN de acceso.
 * `managerPin`: PIN de override de gerente — undefined NO lo toca, '' lo BORRA,
 * 4-8 digitos lo fijan. Es un secreto distinto del de acceso a proposito.
 */
export async function setUserPin(userId: string, pinId: string, pin?: string, managerPin?: string): Promise<{ userId: string; pinId: string }> {
  if (PREVIEW_STUB) { const u = db.users.find((x) => x.id === userId); if (u) u.pinId = pinId; return mock({ userId, pinId }) }
  const res = await gqlClient.mutation(SetStaffUserPinMutation, {
    id: userId, pinId, ...(pin ? { pin } : {}), ...(managerPin !== undefined ? { managerPin } : {}),
  }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.setStaffUserPin
  if (!r) throw new Error('No se pudo fijar el PIN')
  return { userId: r.userId, pinId: r.pinId }
}

// ── compat helpers (used elsewhere; kept as thin wrappers) ─────────────────
/** Toggle a user active/suspended (PUT /api/users/:id { status }). */
export async function setUserActive(id: string, active: boolean): Promise<StaffUser> {
  return updateUser(id, { status: active ? 'ACTIVE' : 'SUSPENDED' })
}
